import { Controller } from "@hotwired/stimulus"
import jsQR from "jsqr"

export default class extends Controller {
  static targets = ["video", "canvas", "result", "sendToField", "toggleButton"]
  static values = { autostart: { type: Boolean, default: false } }

  connect() {
    this.canvasContext = this.canvasTarget.getContext("2d")
    this.rafId = null
    this.stream = null

    if (this.autostartValue) {
      this.start()
    } else {
      this.resultTarget.textContent = "Scanner idle, press ⚡."
      this.updateToggleButton(false)
    }
  }

  disconnect() {
    this.stop()
  }

  start() {
    if (this.stream) return
    this.updateToggleButton(true)
    this.resultTarget.textContent = "Starting scanner..."
    this.startScanner()
  }

  stop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    if (this.videoTarget) {
      this.videoTarget.srcObject = null
    }

    this.updateToggleButton(false)
    this.resultTarget.textContent = "Scanner idle, press ⚡."
  }

  toggle() {
    if (this.stream) {
      this.stop()
    } else {
      this.start()
    }
  }

  startScanner() {
    const constraints = {
      video: {
        facingMode: { ideal: "environment" } // desktop fallback included
      }
    }

    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        this.stream = stream
        this.videoTarget.srcObject = stream
        this.videoTarget.setAttribute("playsinline", true)
        this.videoTarget.play()
        this.rafId = requestAnimationFrame(this.tick.bind(this))
      })
      .catch((err) => {
        console.error("📷 Camera error:", err.name, err.message)
        this.resultTarget.textContent = `Camera error: ${err.name} — ${err.message}`
        this.updateToggleButton(false)

        if (err.name === "NotAllowedError") {
          this.resultTarget.textContent += " — Please allow camera access in your browser settings."
        } else if (err.name === "NotFoundError") {
          this.resultTarget.textContent += " — No camera found on this device."
        }
      })
  }

  tick() {
    if (this.videoTarget.readyState === this.videoTarget.HAVE_ENOUGH_DATA) {
      this.canvasTarget.height = this.videoTarget.videoHeight
      this.canvasTarget.width = this.videoTarget.videoWidth
      this.canvasContext.drawImage(this.videoTarget, 0, 0, this.canvasTarget.width, this.canvasTarget.height)

      const imageData = this.canvasContext.getImageData(0, 0, this.canvasTarget.width, this.canvasTarget.height)
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "attemptBoth"
      })

      if (code && this.hasSendToFieldTarget) {
        const scannedValue = code.data.toString().trim()
        this.resultTarget.textContent = "✅ QR FOUND!"
        if (this.sendToFieldTarget.value !== scannedValue) {
          this.sendToFieldTarget.value = scannedValue
        }
        this.element.dispatchEvent(
          new CustomEvent("scanner:found", {
            bubbles: true,
            detail: { data: scannedValue }
          })
        )
      } else if (!this.hasSendToFieldTarget || !this.sendToFieldTarget.value) {
        this.resultTarget.textContent = "No QR Code detected."
      } else {
        this.resultTarget.textContent = "Scanning..."
      }
    }

    this.rafId = requestAnimationFrame(this.tick.bind(this))
  }

  updateToggleButton(active) {
    if (!this.hasToggleButtonTarget) return
    this.toggleButtonTarget.innerHTML = '<span class="pow-wow-scan-bolt">⚡</span>'
    this.toggleButtonTarget.setAttribute("aria-pressed", active ? "true" : "false")
  }
}
